<ng-template #onboardingOverlay>
  @if (onboarding()) {
    <div
      class="t-modal onboarding-monet-modal flex flex-col rounded-none sm:rounded-xl border-none sm:border border-white/60 dark:border-white/10 shadow-xl overflow-hidden shrink-0 relative"
      [class.is-open]="isOpen()"
      [class.is-closing]="isClosing()"
      [class.bg-card]="!isMobile()"
      [class.onboarding-mobile-view]="isMobile()"
      [style.width]="
        isMobile()
          ? '100%'
          : onboarding().size === 'sm'
            ? '384px'
            : onboarding().size === 'lg'
              ? '768px'
              : '512px'
      "
      [style.height]="isMobile() ? '100%' : modalHeight() + 'px'"
    >
      <!-- Inner wrapper to measure natural auto height -->
      <div
        #modalContent
        class="flex flex-col w-full"
        [class.h-full]="isMobile()"
      >
        <!-- Body Container -->
        <div
          #stepContainer
          class="w-full overflow-y-auto flex flex-col flex-1"
          [class.p-4]="!isMobile()"
          [class.pt-0]="isMobile()"
          [class.px-0]="isMobile()"
          [class.h-full]="isMobile()"
          [class.max-h-[calc(85vh-120px)]]="!isMobile()"
        >
          <div
            class="w-full relative overflow-hidden bg-white/70 dark:bg-neutral-950/55 shrink-0"
            [ngClass]="{
              'h-[50%]': isMobile(),
              'aspect-video min-h-[160px] sm:min-h-[200px] border border-white/60 dark:border-white/10 rounded-lg mb-4 shadow-sm':
                !isMobile(),
            }"
          >
            <!-- Skip Button inside media container (mobile only) -->
            @if (isMobile()) {
              <button
                type="button"
                class="button-outline button-xs absolute top-4 right-4 z-10 bg-black/20 backdrop-blur-md text-white border-white/20 hover:bg-black/40 hover:text-white active:scale-95 transition-all cursor-pointer"
                [disabled]="skipCountdown() > 0"
                (click)="skip()"
              >
                {{
                  skipCountdown() > 0 ? "Skip " + skipCountdown() + "s" : "Skip"
                }}
              </button>
            }

            <div
              class="flex w-full h-full transition-transform duration-300 ease-in-out"
              [style.transform]="'translateX(-' + currentStep() * 100 + '%)'"
            >
              @for (item of onboarding().items; track item; let i = $index) {
                <div
                  class="w-full h-full shrink-0 flex items-center justify-center overflow-hidden"
                >
                  @if (item?.mediaUrl) {
                    @if (item.mediaType === "image") {
                      <img
                        [src]="item.mediaUrl"
                        class="w-full h-full"
                        [class.object-contain]="
                          item.mediaUrl?.includes('presets/') ||
                          item.mediaUrl?.includes('preset')
                        "
                        [class.object-cover]="
                          !item.mediaUrl?.includes('presets/') &&
                          !item.mediaUrl?.includes('preset')
                        "
                        [alt]="item.title || 'Guide'"
                        (load)="updateModalHeight()"
                      />
                    } @else if (item.mediaType === "video") {
                      <app-video-player
                        [src]="item.mediaUrl"
                        [autoplay]="true"
                        [muted]="true"
                        [loop]="true"
                        (loadedmetadata)="updateModalHeight()"
                        class="w-full h-full"
                      ></app-video-player>
                    }
                  } @else {
                    <app-empty-state
                      [compact]="true"
                      [showAction]="false"
                      [label]="item?.mediaType === 'video' ? 'Video Placeholder' : 'Image Placeholder'"
                      [asciiText]="item?.mediaType === 'video' ? 'VIDEO' : 'IMAGE'"
                    ></app-empty-state>
                  }
                </div>
              }
            </div>
          </div>

          <!-- Pagination Indicators -->
          @if (isMobile()) {
            <div class="flex items-center justify-center space-x-2 py-6">
              @for (s of onboarding().items; track s; let i = $index) {
                <div
                  class="w-1.5 h-1.5 rounded-full transition-all duration-300"
                  [ngClass]="
                    currentStep() === i
                      ? 'w-5 bg-blue-600'
                      : 'bg-neutral-300 dark:bg-neutral-700'
                  "
                ></div>
              }
            </div>
          }

          <!-- Title & Description -->
          <div [class.px-8]="isMobile()" [class.text-center]="isMobile()">
            <h5
              class="t-text-swap font-bold mb-2 text-foreground leading-tight"
              [class.text-left]="!isMobile()"
              [class.text-center]="isMobile()"
              [class.text-lg]="!isMobile()"
              [class.text-3xl]="isMobile()"
              [ngClass]="titleSwapState()"
            >
              {{
                onboarding().items[displayedTitleStep()]?.title || "Step title"
              }}
            </h5>
            <p
              class="t-text-swap text-secondary leading-relaxed"
              [class.text-left]="!isMobile()"
              [class.text-center]="isMobile()"
              [class.text-sm]="!isMobile()"
              [class.text-base]="isMobile()"
              [ngClass]="descriptionSwapState()"
            >
              {{
                onboarding().items[displayedDescriptionStep()]?.description ||
                  "No description provided."
              }}
            </p>
          </div>

          @if (!isMobile()) {
            <!-- Pagination Indicators (Desktop) -->
            <div class="flex items-center justify-center space-x-1.5 pt-4">
              @for (s of onboarding().items; track s; let i = $index) {
                <div
                  class="w-1.5 h-1.5 rounded-full transition-all duration-300"
                  [ngClass]="
                    currentStep() === i
                      ? 'w-4 bg-blue-600'
                      : 'bg-white/80 ring-1 ring-neutral-300/80 dark:bg-neutral-300/70 dark:ring-white/10'
                  "
                ></div>
              }
            </div>
          }
        </div>

        <!-- Modal Footer -->
        <div
          #modalFooter
          class="flex shrink-0 w-full"
          [ngClass]="{
            'p-4 items-center justify-between gap-3 border-t border-white/50 bg-white/45 backdrop-blur-md dark:border-white/10 dark:bg-neutral-950/30':
              !isMobile(),
            'flex-col px-8 pb-12 pt-4 gap-3': isMobile(),
          }"
        >
          @if (!isMobile()) {
            <div class="flex items-center gap-3 min-w-0">
              <button
                type="button"
                class="button-tertiary"
                [disabled]="skipCountdown() > 0"
                (click)="skip()"
              >
                {{
                  skipCountdown() > 0 ? "Skip " + skipCountdown() + "s" : "Skip"
                }}
              </button>
            </div>
          }

          <!-- Action Buttons -->
          <div
            class="flex"
            [class.gap-2]="!isMobile()"
            [class.flex-col]="isMobile()"
            [class.w-full]="isMobile()"
            [class.gap-3]="isMobile()"
          >
            <button
              type="button"
              class="button-primary"
              [class.w-full]="isMobile()"
              [class.order-1]="isMobile()"
              [class.order-2]="!isMobile()"
              (click)="next()"
            >
              <span class="t-text-swap" [ngClass]="buttonSwapState()">
                {{
                  displayedButtonStep() === onboarding().items.length - 1
                    ? "Finish"
                    : "Next"
                }}
              </span>
            </button>
            <button
              type="button"
              class="button-outline"
              [class.w-full]="isMobile()"
              [class.order-2]="isMobile()"
              [class.order-1]="!isMobile()"
              [class.border-transparent]="isMobile()"
              [class.bg-neutral-100]="isMobile()"
              [ngClass]="{ 'dark:bg-neutral-800': isMobile() }"
              [disabled]="currentStep() === 0"
              (click)="prev()"
            >
              Back
            </button>
          </div>
        </div>
      </div>
    </div>
  }
</ng-template>

@if (showFloatingHelpButton()) {
  <button
    (click)="replayGuide()"
    type="button"
    class="fixed bottom-6 right-6 z-[999] p-0 flex items-center justify-center bg-neutral-900/50 dark:bg-black/50 backdrop-blur border border-white/10 text-neutral-200 hover:text-white rounded-full shadow-lg hover:bg-blue-600 hover:border-blue-600 dark:hover:bg-blue-600 dark:hover:border-blue-600 hover:scale-105 active:scale-95 transition-all duration-200 ease-in-out pointer-events-auto"
    [class.w-10]="floatingButtonSize() === 'large'"
    [class.h-10]="floatingButtonSize() === 'large'"
    [class.w-7]="floatingButtonSize() === 'small'"
    [class.h-7]="floatingButtonSize() === 'small'"
    [matTooltip]="'Replay guide'"
  >
    <mat-icon
      [class.icon-size-6]="floatingButtonSize() === 'large'"
      [class.icon-size-4]="floatingButtonSize() === 'small'"
      class="text-white"
      [svgIcon]="'heroicons_outline:question-mark-circle'"
    ></mat-icon>
  </button>
}
